import React, { useEffect, useMemo, useState, useRef } from 'react';
import axios from 'axios';
import { getSessionTokenForUploads } from '@/utils/uploadDocument';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { DataRoomCollaborators } from '@/components/DataRoomCollaborators';
import ShareLinkCreateDialog from '@/components/ShareLinkCreateDialog';
import {
  FolderLock,
  Upload,
  Download,
  Trash2,
  Link as LinkIcon,
  Copy,
  Ban,
  Eye,
  Shield,
  FileText,
  Loader2,
  Plus,
  Lock,
  Activity,
  Clock,
  Users,
  X,
  RefreshCw,
  Files,
  CalendarClock,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;

const CATEGORIES = [
  { value: 'pitch_deck', label: 'Pitch Deck / Overview' },
  { value: 'preclinical', label: 'Preclinical' },
  { value: 'clinical', label: 'Clinical' },
  { value: 'cmc', label: 'CMC / Manufacturing' },
  { value: 'regulatory', label: 'Regulatory' },
  { value: 'ip', label: 'IP / Patents' },
  { value: 'financials', label: 'Financials' },
  { value: 'team', label: 'Team & Corporate' },
  { value: 'market', label: 'Market / Commercial' },
  { value: 'misc', label: 'Misc / Other' },
];

const CAT_LABEL = Object.fromEntries(CATEGORIES.map((c) => [c.value, c.label]));

function fmtBytes(b) {
  if (!b) return '';
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  if (b < 1024 * 1024 * 1024) return `${(b / 1024 / 1024).toFixed(1)} MB`;
  return `${(b / 1024 / 1024 / 1024).toFixed(2)} GB`;
}

function fmtDate(s) {
  if (!s) return '';
  try {
    return new Date(s).toLocaleDateString();
  } catch {
    return s;
  }
}

function fmtDateTime(s) {
  if (!s) return '';
  try {
    return new Date(s).toLocaleString();
  } catch {
    return s;
  }
}

function fmtRelative(s) {
  if (!s) return '';
  try {
    const t = new Date(s).getTime();
    const diff = Date.now() - t;
    if (diff < 0) return fmtDateTime(s);
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'Just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    if (days < 7) return `${days}d ago`;
    if (days < 30) return `${Math.floor(days / 7)}w ago`;
    return new Date(s).toLocaleDateString();
  } catch {
    return s;
  }
}

const ACTION_LABEL = {
  open: 'Unlocked',
  view: 'Viewed',
  download: 'Downloaded',
  zip_download: 'Downloaded all (ZIP)',
};

const ACTION_STYLE = {
  open: 'text-slate-600 bg-slate-100',
  view: 'text-blue-700 bg-blue-100',
  download: 'text-emerald-700 bg-emerald-100',
  zip_download: 'text-amber-700 bg-amber-100',
};

export default function DataRoomPanel({ biotechId, biotechName, assets = [] }) {
  const [open, setOpen] = useState(false);
  const [files, setFiles] = useState([]);
  const [shares, setShares] = useState([]);
  const [eligiblePharmas, setEligiblePharmas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [tab, setTab] = useState('files');

  const fileCount = files.length;

  const loadAll = async () => {
    setLoading(true);
    try {
      const [fRes, sRes, pRes] = await Promise.all([
        axios.get(`${API}/data-room?biotech_id=${biotechId}`, { withCredentials: true }),
        axios.get(`${API}/data-room/shares?biotech_id=${biotechId}`, { withCredentials: true }),
        axios.get(`${API}/data-room/${biotechId}/eligible-pharmas`, { withCredentials: true }),
      ]);
      setFiles(fRes.data?.files || []);
      setShares(sRes.data?.shares || []);
      setEligiblePharmas(pRes.data?.pharmas || []);
    } catch (e) {
      console.error('[DataRoom] load failed', e);
      toast.error('Failed to load Data Room');
    } finally {
      setLoading(false);
    }
  };

  // Lightweight count fetch for the closed tile (separate so opening the modal
  // doesn't trigger a re-fetch every render).
  useEffect(() => {
    if (!biotechId) return;
    axios
      .get(`${API}/data-room?biotech_id=${biotechId}`, { withCredentials: true })
      .then((r) => setFiles(r.data?.files || []))
      .catch(() => {});
  }, [biotechId]);

  useEffect(() => {
    if (open) loadAll();
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid="data-room-open-btn"
        className="w-full flex items-center justify-between px-4 py-3 rounded-lg border border-slate-200 hover:border-amber-300 hover:bg-amber-50/40 transition group"
      >
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-md bg-amber-100 text-amber-700 flex items-center justify-center">
            <FolderLock className="w-4 h-4" />
          </div>
          <div className="text-left">
            <div className="text-sm font-medium text-slate-900">Data Room</div>
            <div className="text-xs text-slate-500">
              {fileCount} {fileCount === 1 ? 'file' : 'files'} · confidential, CDA-gated
            </div>
          </div>
        </div>
        <span className="text-xs text-amber-700 group-hover:underline">Manage</span>
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto" data-testid="data-room-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FolderLock className="w-5 h-5 text-amber-700" />
              {biotechName} · Data Room
            </DialogTitle>
            <DialogDescription>
              Confidential dossier. Files are never AI-enriched. Shareable links require a Finalised
              Buyer CDA on the recipient pharma.
            </DialogDescription>
          </DialogHeader>

          <Tabs value={tab} onValueChange={setTab}>
            <TabsList>
              <TabsTrigger value="files" data-testid="data-room-tab-files">
                Files ({files.length})
              </TabsTrigger>
              <TabsTrigger value="shares" data-testid="data-room-tab-shares">
                Shareable Links ({shares.filter((s) => !s.is_revoked).length})
              </TabsTrigger>
            </TabsList>

            <TabsContent value="files">
              <FilesTab
                files={files}
                assets={assets}
                biotechId={biotechId}
                uploading={uploading}
                setUploading={setUploading}
                onChange={loadAll}
              />
            </TabsContent>

            <TabsContent value="shares">
              <SharesTab
                shares={shares}
                files={files}
                assets={assets}
                biotechId={biotechId}
                biotechName={biotechName}
                eligiblePharmas={eligiblePharmas}
                onChange={loadAll}
              />
            </TabsContent>
          </Tabs>

          {loading && (
            <div className="flex items-center gap-2 text-xs text-slate-500 mt-2">
              <Loader2 className="w-3 h-3 animate-spin" />
              Refreshing…
            </div>
          )}

          <div className="sticky bottom-0 -mx-6 px-6 pt-3 pb-1 bg-gradient-to-t from-white via-white to-transparent flex justify-end">
            <Button
              onClick={() => setOpen(false)}
              data-testid="data-room-done-btn"
              className="bg-slate-900 hover:bg-slate-800"
            >
              Done
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

// ─── Files tab ────────────────────────────────────────────────────────────

function FilesTab({ files, assets, biotechId, uploading, setUploading, onChange }) {
  const inputRef = useRef(null);
  const [category, setCategory] = useState('misc');
  const [assetId, setAssetId] = useState('__company__'); // company-level by default
  const [groupBy, setGroupBy] = useState('category'); // 'category' | 'asset'

  const handleFiles = async (fileList) => {
    if (!fileList || !fileList.length) return;
    setUploading(true);
    const sessionToken = await getSessionTokenForUploads();
    const results = { ok: 0, fail: 0, dup: 0 };
    for (const f of Array.from(fileList)) {
      const fd = new FormData();
      fd.append('file', f);
      fd.append('biotech_id', biotechId);
      fd.append('category', category);
      if (assetId && assetId !== '__company__') fd.append('asset_id', assetId);
      if (sessionToken) fd.append('session_token', sessionToken);
      try {
        await axios.post(`${API}/data-room/upload`, fd, {
          withCredentials: true,
          headers: sessionToken ? { Authorization: `Bearer ${sessionToken}` } : {},
        });
        results.ok += 1;
      } catch (e) {
        const status = e?.response?.status;
        if (status === 409) {
          results.dup += 1;
        } else {
          results.fail += 1;
          console.error('[DataRoom] upload failed', f.name, e?.response?.data || e);
        }
      }
    }
    setUploading(false);
    if (results.ok) toast.success(`Uploaded ${results.ok} file(s)`);
    if (results.dup) toast.message(`${results.dup} duplicate(s) skipped`);
    if (results.fail) toast.error(`${results.fail} upload(s) failed`);
    onChange();
    if (inputRef.current) inputRef.current.value = '';
  };

  const handleDelete = async (fileId, name) => {
    if (!window.confirm(`Remove "${name}" from the Data Room?`)) return;
    try {
      await axios.delete(`${API}/data-room/${fileId}`, { withCredentials: true });
      toast.success('File removed');
      onChange();
    } catch (e) {
      toast.error('Delete failed');
    }
  };

  const handleDownload = async (fileId, name) => {
    try {
      const res = await axios.get(`${API}/documents/${fileId}/download`, {
        withCredentials: true,
        responseType: 'blob',
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = name || 'file';
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) {
      toast.error('Download failed');
    }
  };

  // Grouping
  const grouped = useMemo(() => {
    const out = {};
    for (const f of files) {
      const key =
        groupBy === 'asset'
          ? f.data_room_asset_id
            ? assets.find((a) => a.id === f.data_room_asset_id)?.asset_name ||
              assets.find((a) => a.id === f.data_room_asset_id)?.name ||
              'Linked asset (deleted)'
            : 'Company-level'
          : CAT_LABEL[f.data_room_category] || 'Misc / Other';
      if (!out[key]) out[key] = [];
      out[key].push(f);
    }
    return out;
  }, [files, groupBy, assets]);

  return (
    <div className="space-y-4 mt-3">
      {/* Session 1 · Collaborators & activity — admin can invite Brian
          Kim (owner) + Lynn (editor) so they upload without ForesightRx
          gatekeeping. Non-collaborators see nothing (component returns
          null when they lack access). */}
      <DataRoomCollaborators biotechId={biotechId} />

      {/* Upload bar */}
      <div className="rounded-lg border border-dashed border-amber-300 bg-amber-50/50 p-4">
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex-1 min-w-[180px]">
            <Label className="text-xs">Category</Label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger data-testid="data-room-upload-category" className="h-9">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {CATEGORIES.map((c) => (
                  <SelectItem key={c.value} value={c.value}>
                    {c.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex-1 min-w-[180px]">
            <Label className="text-xs">Link to asset (optional)</Label>
            <Select value={assetId} onValueChange={setAssetId}>
              <SelectTrigger data-testid="data-room-upload-asset" className="h-9">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__company__">Company-level (no asset)</SelectItem>
                {assets.map((a) => (
                  <SelectItem key={a.id} value={a.id}>
                    {a.asset_name || a.name || 'Unnamed asset'}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <input
              ref={inputRef}
              type="file"
              multiple
              hidden
              onChange={(e) => handleFiles(e.target.files)}
              data-testid="data-room-file-input"
            />
            <Button
              size="sm"
              onClick={() => inputRef.current?.click()}
              disabled={uploading}
              data-testid="data-room-upload-btn"
              className="bg-amber-600 hover:bg-amber-700"
            >
              {uploading ? (
                <>
                  <Loader2 className="w-3 h-3 mr-1 animate-spin" /> Uploading…
                </>
              ) : (
                <>
                  <Upload className="w-3 h-3 mr-1" /> Upload files
                </>
              )}
            </Button>
          </div>
        </div>
        <div className="text-[11px] text-slate-500 mt-2">
          Multi-select supported. ZIPs welcome (no enrichment runs on these).
        </div>
      </div>

      {/* Group toggle */}
      <div className="flex items-center justify-between">
        <div className="text-xs text-slate-500">{files.length} file(s) in Data Room</div>
        <div className="flex items-center gap-1">
          <span className="text-xs text-slate-500">Group by:</span>
          <button
            onClick={() => setGroupBy('category')}
            className={`text-xs px-2 py-0.5 rounded ${
              groupBy === 'category' ? 'bg-amber-100 text-amber-800' : 'text-slate-500 hover:bg-slate-100'
            }`}
          >
            Category
          </button>
          <button
            onClick={() => setGroupBy('asset')}
            className={`text-xs px-2 py-0.5 rounded ${
              groupBy === 'asset' ? 'bg-amber-100 text-amber-800' : 'text-slate-500 hover:bg-slate-100'
            }`}
          >
            Asset
          </button>
        </div>
      </div>

      {/* Files */}
      {files.length === 0 ? (
        <div className="text-center py-10 text-slate-400 text-sm">
          No files yet. Upload to get started.
        </div>
      ) : (
        <div className="space-y-4">
          {Object.entries(grouped).map(([groupName, groupFiles]) => (
            <div key={groupName}>
              <div className="text-xs font-semibold text-slate-700 uppercase tracking-wide mb-2">
                {groupName} <span className="text-slate-400">({groupFiles.length})</span>
              </div>
              <div className="space-y-1">
                {groupFiles.map((f) => (
                  <div
                    key={f.id}
                    className="flex items-center justify-between gap-2 px-3 py-2 rounded border border-slate-200 hover:bg-slate-50"
                  >
                    <div className="flex items-center gap-2 min-w-0 flex-1">
                      <FileText className="w-4 h-4 text-slate-400 flex-shrink-0" />
                      <div className="min-w-0">
                        <div className="text-sm truncate">{f.original_filename}</div>
                        <div className="text-[11px] text-slate-500">
                          {fmtBytes(f.size)} · {fmtDate(f.created_at)}
                          {f.data_room_category && groupBy !== 'category' && (
                            <>
                              {' '}· <span className="text-amber-700">{CAT_LABEL[f.data_room_category]}</span>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleDownload(f.id, f.original_filename)}
                        data-testid={`data-room-download-${f.id}`}
                      >
                        <Download className="w-3 h-3" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleDelete(f.id, f.original_filename)}
                        data-testid={`data-room-delete-${f.id}`}
                        className="text-rose-600 hover:text-rose-700 hover:bg-rose-50"
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Shares tab ───────────────────────────────────────────────────────────

function SharesTab({ shares, files, assets, biotechId, biotechName, eligiblePharmas, onChange }) {
  const [showCreateDialog, setShowCreateDialog] = useState(false);

  const handleRevoke = async (shareId, pharma) => {
    if (!window.confirm(`Revoke link shared with ${pharma}? They will lose access immediately.`)) return;
    try {
      await axios.delete(`${API}/data-room/shares/${shareId}`, { withCredentials: true });
      toast.success('Link revoked');
      onChange();
    } catch (e) {
      toast.error('Revoke failed');
    }
  };

  const handleRegeneratePassword = async (shareId) => {
    if (!window.confirm('Regenerate password? The current password will stop working immediately.')) return;
    try {
      const res = await axios.post(`${API}/data-room/shares/${shareId}/regenerate-password`, {}, { withCredentials: true });
      const newPwd = res.data?.password_plain;
      if (newPwd) {
        navigator.clipboard.writeText(newPwd);
        toast.success(`New password copied: ${newPwd}`);
      } else {
        toast.success('Password regenerated');
      }
      onChange();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Regenerate failed');
    }
  };

  const handleDuplicate = async (shareId, pharma) => {
    try {
      await axios.post(`${API}/data-room/shares/${shareId}/duplicate`, {}, { withCredentials: true });
      toast.success(`Cloned share for ${pharma} — new link + password generated`);
      onChange();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Duplicate failed');
    }
  };

  const copyLink = (token, password) => {
    const url = `${window.location.origin}/dataroom/${token}`;
    const text = `Data Room access\nLink: ${url}\nPassword: ${password}`;
    navigator.clipboard.writeText(text);
    toast.success('Link + password copied to clipboard');
  };

  const copyPassword = (password) => {
    navigator.clipboard.writeText(password);
    toast.success('Password copied');
  };

  return (
    <div className="space-y-5 mt-3">
      {/* Create new share — modal-launched */}
      <div className="rounded-lg border border-emerald-200 bg-gradient-to-br from-emerald-50/40 to-sky-50/40 p-4 flex items-center justify-between gap-3">
        <div>
          <div className="text-sm font-semibold text-slate-900">Create a secure share link</div>
          <div className="text-[11px] text-slate-500 mt-0.5">
            Advanced controls — scope, expiry, downloads, NDA gate, watermark, and per-recipient caps.
          </div>
        </div>
        <Button
          onClick={() => setShowCreateDialog(true)}
          className="bg-emerald-600 hover:bg-emerald-700"
          data-testid="open-share-create-dialog"
        >
          <Plus className="w-4 h-4 mr-1.5" /> New share link
        </Button>
      </div>

      <ShareLinkCreateDialog
        open={showCreateDialog}
        onClose={() => setShowCreateDialog(false)}
        biotechId={biotechId}
        biotechName={biotechName}
        assets={assets}
        onCreated={() => onChange()}
      />

      {/* Existing shares */}
      <div>
        <div className="text-sm font-semibold text-slate-800 mb-2">All share links</div>
        {shares.length === 0 ? (
          <div className="text-xs text-slate-400 text-center py-8">No share links created yet.</div>
        ) : (
          <div className="space-y-2">
            {shares.map((s) => (
              <ShareRow
                key={s.id}
                share={s}
                onRevoke={() => handleRevoke(s.id, s.recipient_pharma_name)}
                onCopy={() => copyLink(s.token, s.password_plain)}
                onCopyPassword={() => copyPassword(s.password_plain)}
                onRegeneratePassword={() => handleRegeneratePassword(s.id)}
                onDuplicate={() => handleDuplicate(s.id, s.recipient_pharma_name)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function ShareRow({ share, onRevoke, onCopy, onCopyPassword, onRegeneratePassword, onDuplicate }) {
  const [showActivity, setShowActivity] = useState(false);
  const url = `${window.location.origin}/dataroom/${share.token}`;
  const isRevoked = share.is_revoked;
  const metrics = share.metrics || {};
  const lastAccessed = metrics.last_accessed_at;
  const filesViewed = metrics.unique_files_viewed || 0;
  const filesDownloaded = metrics.unique_files_downloaded || 0;
  const opens = metrics.opens || 0;
  const uniqueViewers = metrics.unique_viewers || 0;
  const hasActivity = !!lastAccessed;

  // Phase-1 v15 additions
  const expiresAt = share.expires_at;
  const isExpired = expiresAt && new Date(expiresAt) <= new Date();
  const downloadPermission = share.download_permission || 'preview_download';
  const ndaRequired = !!share.nda_required;
  const maxDl = share.max_downloads_per_recipient;
  const watermarkEnabled = !!share.watermark_enabled;
  const scope = share.scope || (share.asset_id ? 'asset' : 'entire');

  const DP_LABELS = {
    preview_download: { label: 'Preview + Download', tone: 'emerald' },
    preview_only:     { label: 'Preview Only',       tone: 'blue' },
    download_all:     { label: 'Download All (ZIP)', tone: 'indigo' },
    disabled:         { label: 'Downloads Disabled', tone: 'rose' },
  };
  const dpMeta = DP_LABELS[downloadPermission] || DP_LABELS.preview_download;
  const dpBadgeClass = {
    emerald: 'text-emerald-700 border-emerald-200 bg-emerald-50',
    blue:    'text-blue-700 border-blue-200 bg-blue-50',
    indigo:  'text-indigo-700 border-indigo-200 bg-indigo-50',
    rose:    'text-rose-700 border-rose-200 bg-rose-50',
  }[dpMeta.tone];

  return (
    <div
      className={`border rounded-lg p-3 ${
        isRevoked ? 'border-slate-200 bg-slate-50 opacity-70'
        : isExpired ? 'border-rose-200 bg-rose-50/40'
        : 'border-slate-200 hover:border-slate-300'
      }`}
      data-testid={`share-row-${share.id}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="text-sm font-semibold text-slate-900">{share.recipient_pharma_name}</div>
            {isRevoked ? (
              <Badge variant="outline" className="text-rose-700 border-rose-200 bg-rose-50">Revoked</Badge>
            ) : isExpired ? (
              <Badge variant="outline" className="text-rose-700 border-rose-200 bg-rose-50">Expired</Badge>
            ) : hasActivity ? (
              <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100">Active · engaged</Badge>
            ) : (
              <Badge className="bg-slate-100 text-slate-700 hover:bg-slate-100">Active · no activity yet</Badge>
            )}
            {scope === 'asset' && (
              <Badge variant="outline" className="text-amber-700 border-amber-200 bg-amber-50">Asset-scoped</Badge>
            )}
            {scope === 'assets' && (
              <Badge variant="outline" className="text-amber-700 border-amber-200 bg-amber-50">Multi-asset</Badge>
            )}
            {scope === 'folders' && (
              <Badge variant="outline" className="text-amber-700 border-amber-200 bg-amber-50">Folder-scoped</Badge>
            )}
            <Badge variant="outline" className={dpBadgeClass}>{dpMeta.label}</Badge>
            {ndaRequired && (
              <Badge variant="outline" className="text-indigo-700 border-indigo-200 bg-indigo-50">NDA gate</Badge>
            )}
            {watermarkEnabled && (
              <Badge variant="outline" className="text-purple-700 border-purple-200 bg-purple-50">Watermark</Badge>
            )}
          </div>
          <div className="text-[11px] text-slate-500 mt-0.5">
            Created {fmtDate(share.created_at)} by {share.created_by_email || 'admin'}
            {share.note && <> · {share.note}</>}
            {expiresAt && (
              <> · <span className={isExpired ? 'text-rose-600 font-medium' : ''}>
                {isExpired ? 'Expired ' : 'Expires '}{fmtDate(expiresAt)}
              </span></>
            )}
            {maxDl && <> · Max {maxDl} downloads/recipient</>}
          </div>

          {/* Engagement metrics row */}
          <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-2">
            <MetricTile
              icon={<Clock className="w-3 h-3" />}
              label="Last accessed"
              value={lastAccessed ? fmtRelative(lastAccessed) : '—'}
              hint={lastAccessed ? fmtDateTime(lastAccessed) : 'Never opened'}
              tone={lastAccessed ? 'emerald' : 'slate'}
              testid={`metric-last-${share.id}`}
            />
            <MetricTile
              icon={<Eye className="w-3 h-3" />}
              label="Files viewed"
              value={filesViewed}
              hint={`${uniqueViewers || 0} unique ${uniqueViewers === 1 ? 'recipient' : 'recipients'}`}
              tone={filesViewed > 0 ? 'blue' : 'slate'}
              testid={`metric-viewed-${share.id}`}
            />
            <MetricTile
              icon={<Download className="w-3 h-3" />}
              label="Files downloaded"
              value={filesDownloaded}
              hint={`${metrics.total_downloads || 0} total download${metrics.total_downloads === 1 ? '' : 's'}`}
              tone={filesDownloaded > 0 ? 'amber' : 'slate'}
              testid={`metric-downloaded-${share.id}`}
            />
            <MetricTile
              icon={<Activity className="w-3 h-3" />}
              label="Session opens"
              value={opens}
              hint={opens === 0 ? 'Not opened yet' : 'Unlock events'}
              tone={opens > 0 ? 'indigo' : 'slate'}
              testid={`metric-opens-${share.id}`}
            />
          </div>

          <div className="mt-3 text-xs font-mono text-slate-600 truncate">{url}</div>
          <div className="text-[11px] text-slate-500 mt-1 flex items-center gap-2 flex-wrap">
            <span>
              Password: <code className="bg-slate-100 px-1 rounded">{share.password_plain}</code>
            </span>
            <button
              onClick={() => setShowActivity(true)}
              disabled={!hasActivity}
              className={`inline-flex items-center gap-1 rounded px-1.5 py-0.5 ${
                hasActivity
                  ? 'text-blue-700 hover:bg-blue-50 hover:underline'
                  : 'text-slate-300 cursor-not-allowed'
              }`}
              data-testid={`view-activity-${share.id}`}
            >
              <Activity className="w-3 h-3" />
              View activity timeline
            </button>
          </div>
        </div>
        <div className="flex flex-col gap-1 flex-shrink-0 w-32">
          {!isRevoked && !isExpired && (
            <>
              <Button size="sm" variant="outline" onClick={onCopy} data-testid={`copy-share-${share.id}`}>
                <Copy className="w-3 h-3 mr-1" /> Copy link
              </Button>
              <Button size="sm" variant="outline" onClick={onCopyPassword} data-testid={`copy-pwd-${share.id}`}>
                <Lock className="w-3 h-3 mr-1" /> Copy password
              </Button>
              <Button size="sm" variant="outline" onClick={onRegeneratePassword} data-testid={`regen-pwd-${share.id}`}>
                <RefreshCw className="w-3 h-3 mr-1" /> New password
              </Button>
              <Button size="sm" variant="outline" onClick={onDuplicate} data-testid={`dup-share-${share.id}`}>
                <Files className="w-3 h-3 mr-1" /> Duplicate
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={onRevoke}
                data-testid={`revoke-share-${share.id}`}
                className="text-rose-600 hover:text-rose-700"
              >
                <Ban className="w-3 h-3 mr-1" /> Revoke
              </Button>
            </>
          )}
          {isExpired && !isRevoked && (
            <Button size="sm" variant="outline" onClick={onDuplicate} data-testid={`dup-share-${share.id}`}>
              <Files className="w-3 h-3 mr-1" /> Duplicate as new
            </Button>
          )}
        </div>
      </div>

      {showActivity && (
        <ShareActivityModal
          shareId={share.id}
          pharmaName={share.recipient_pharma_name}
          onClose={() => setShowActivity(false)}
        />
      )}
    </div>
  );
}

function MetricTile({ icon, label, value, hint, tone = 'slate', testid }) {
  const toneClasses = {
    slate: 'bg-slate-50 text-slate-500 border-slate-200',
    emerald: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    blue: 'bg-blue-50 text-blue-700 border-blue-200',
    amber: 'bg-amber-50 text-amber-700 border-amber-200',
    indigo: 'bg-indigo-50 text-indigo-700 border-indigo-200',
  };
  return (
    <div
      className={`rounded border px-2 py-1.5 ${toneClasses[tone] || toneClasses.slate}`}
      data-testid={testid}
    >
      <div className="flex items-center gap-1 text-[10px] uppercase tracking-wide opacity-80">
        {icon}
        {label}
      </div>
      <div className="text-sm font-semibold text-slate-900 mt-0.5 truncate" title={typeof hint === 'string' ? hint : ''}>
        {value}
      </div>
      {hint && <div className="text-[10px] text-slate-500 truncate">{hint}</div>}
    </div>
  );
}

function ShareActivityModal({ shareId, pharmaName, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let alive = true;
    setLoading(true);
    axios
      .get(`${API}/data-room/shares/${shareId}/activity`, { withCredentials: true })
      .then((r) => {
        if (alive) setData(r.data);
      })
      .catch(() => {
        if (alive) setError('Failed to load activity');
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [shareId]);

  const events = data?.events || [];
  const metrics = data?.metrics || {};

  // Group events by day for a scannable timeline.
  const grouped = useMemo(() => {
    const buckets = {};
    for (const e of events) {
      const day = e.ts ? new Date(e.ts).toLocaleDateString() : 'Unknown';
      if (!buckets[day]) buckets[day] = [];
      buckets[day].push(e);
    }
    return buckets;
  }, [events]);

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="share-activity-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Activity className="w-4 h-4 text-blue-600" />
            Activity timeline · {pharmaName}
          </DialogTitle>
          <DialogDescription>
            Every unlock, preview and download logged on this share link, newest first.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="py-10 flex items-center justify-center text-slate-400">
            <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading activity…
          </div>
        ) : error ? (
          <div className="py-10 text-center text-rose-600 text-sm">{error}</div>
        ) : events.length === 0 ? (
          <div className="py-10 text-center text-slate-400 text-sm">
            <Eye className="w-6 h-6 mx-auto mb-2 text-slate-300" />
            No activity yet. Once the recipient opens the link, everything they do here shows up in real time.
          </div>
        ) : (
          <>
            {/* Summary strip */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-4">
              <MetricTile
                icon={<Clock className="w-3 h-3" />}
                label="Last accessed"
                value={metrics.last_accessed_at ? fmtRelative(metrics.last_accessed_at) : '—'}
                hint={metrics.last_accessed_at ? fmtDateTime(metrics.last_accessed_at) : ''}
                tone="emerald"
              />
              <MetricTile
                icon={<Eye className="w-3 h-3" />}
                label="Files viewed"
                value={metrics.unique_files_viewed || 0}
                hint="Unique files touched"
                tone="blue"
              />
              <MetricTile
                icon={<Download className="w-3 h-3" />}
                label="Files downloaded"
                value={metrics.unique_files_downloaded || 0}
                hint={`${metrics.total_downloads || 0} download event(s)`}
                tone="amber"
              />
              <MetricTile
                icon={<Users className="w-3 h-3" />}
                label="Unique viewers"
                value={metrics.unique_viewers || 0}
                hint={`${metrics.opens || 0} session opens`}
                tone="indigo"
              />
            </div>

            <div className="space-y-4">
              {Object.entries(grouped).map(([day, items]) => (
                <div key={day}>
                  <div className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide mb-1">
                    {day}
                  </div>
                  <div className="border border-slate-200 rounded-lg overflow-hidden">
                    <table className="w-full text-xs">
                      <thead className="bg-slate-50 text-slate-500">
                        <tr>
                          <th className="text-left px-3 py-2 font-medium">Time</th>
                          <th className="text-left px-3 py-2 font-medium">Recipient</th>
                          <th className="text-left px-3 py-2 font-medium">Action</th>
                          <th className="text-left px-3 py-2 font-medium">File</th>
                        </tr>
                      </thead>
                      <tbody>
                        {items.map((e, i) => (
                          <tr
                            key={i}
                            className="border-t border-slate-100 hover:bg-slate-50"
                            data-testid={`activity-row-${e.action}-${i}`}
                          >
                            <td className="px-3 py-2 text-slate-600 whitespace-nowrap">
                              {e.ts ? new Date(e.ts).toLocaleTimeString() : ''}
                            </td>
                            <td className="px-3 py-2 text-slate-700 truncate max-w-[180px]">
                              {e.email || <span className="text-slate-300">—</span>}
                            </td>
                            <td className="px-3 py-2">
                              <span
                                className={`inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-medium ${
                                  ACTION_STYLE[e.action] || 'text-slate-600 bg-slate-100'
                                }`}
                              >
                                {ACTION_LABEL[e.action] || e.action}
                              </span>
                            </td>
                            <td className="px-3 py-2 text-slate-700 truncate max-w-[240px]">
                              {e.filename || (
                                <span className="text-slate-300">
                                  {e.action === 'zip_download' ? 'All files (ZIP)' : e.action === 'open' ? 'Share unlocked' : '—'}
                                </span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        <div className="flex justify-end mt-4">
          <Button variant="outline" onClick={onClose} data-testid="close-activity-modal">
            <X className="w-3 h-3 mr-1" /> Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
